import type { BorderBandKind, DaedalusGameAdapter, EntityTypeDefinition, JsonSchema, MapDocument, ObjectTypeDefinition, ValidationIssue, ValidationScope } from "daedalus-core";
import { COLLISION_BRUSHES, collisionRulesFor } from "../collision/flags.ts";
import { CREATURE_SPAWN_SCHEMA, spawnIssues } from "../creatures/spawn.ts";
import { NPC_SCHEMA, npcIssues } from "../npcs/marker.ts";
import { fieldIssues } from "../farming/field-map.ts";
import { FIELD_SCHEMA } from "../farming/field-marker.ts";
import { QUEST_POINT_SCHEMA, questPointIssues } from "../quests/point.ts";
import { PROP_FOOTPRINT_RULES, propIsBuilding, propIsGround } from "../collision/footprints.ts";
import { SURFACES, groundSurface } from "../ground/surfaces.ts";
import { CHEST_SCHEMA, chestIssues } from "../items/chest.ts";
import { DEFAULT_READING_LAYOUT, READING_LAYOUTS } from "../interaction/reading.ts";
import { AUTO_CLOSE_GROUPS, autoCloseIssues } from "../interaction/auto-close.ts";
import { GATE_SCHEMA, UNLOCKED_GUARD, lockIssues, lockOf } from "../interaction/lock.ts";
import { assertVerbNames } from "../interaction/verbs.ts";
import { loadRules } from "../rules/loader.ts";
import type { Rules } from "../rules/types.ts";
import { MAX_INTERACTION_TEXT_CHARS } from "../protocol/limits.ts";
import { objectKindOf } from "../protocol/kinds.ts";
import { MAX_RESPAWN_POINT_NAME_CHARS } from "../protocol/respawn.ts";
import { MAX_ANIM } from "../protocol/snapshot.ts";
import { ODYSSEY_CAMERA } from "./camera.ts";
import { ODYSSEY_DENSITY } from "./density.ts";
import { ODYSSEY_STAND_IN } from "./stand-in.ts";
import { REGION_LABEL } from "../world/region.ts";
import { SHRINE_REACH } from "../shrines/reach.ts";
import { KNOWN_FROM_START_FIELD, MAX_PLACE_REGION_CHARS, REGION_FIELD, TOWN_FIELD } from "../shrines/places.ts";
import { SHRINE_START_FIELD } from "../shrines/start.ts";
import { seamGroundOf } from "./seam-ground.ts";
import { shrineIssues } from "./shrine-issues.ts";
import { shrineStartIssues } from "./shrine-start.ts";
import { suggestAnimationName, suggestFrameName } from "./suggest-frame.ts";
import { ARRIVAL_POINT, AREA, CHEST, CREATURE_SPAWN, DUMMY, FIELD, GATE, LANTERN, NPC, QUEST_POINT, ROOF, SHRINE, SIGN, SPAWN_POINT, WAYPOINT, WELL } from "./type-names.ts";
import { ZONE_TYPES } from "../zones/zones.ts";
import { MAP_AMBIENCE, MAP_MUSIC } from "../audio/atmosphere.ts";
import { SOUND_SET_FIELD, soundSetIssues, soundSetLabels, soundSetProperty } from "../audio/thing-sound-field.ts";
import { DOOR_FIELDS } from "../zones/doors.ts";
import { ARRIVAL_NAME_MOST, arrivalIssues } from "./arrival-issues.ts";
import { doorCombatIssues } from "./door-combat-issues.ts";

/**
 * OdysseyTales' registration with Daedalus (ADR-024, ADR-028): the entity types Phase 0 places (Playbook
 * 7.6) and the server-only creature spawn (ADR-082 decision 3), the collision rules and brushes, the zone types its authors paint (ADR-054's amendment of 2026-09-27), the density meter's groups and budgets (ADR-088),
 * the surface a terrain or a tileset is made of, by its name's words (ADR-085's amendment for P1.9-T84),
 * and the rule that every map needs somewhere to spawn. A shrine is an object type (ADR-096) and no entity type: a
 * marker it was saved as is carried to an obelisk as a document is read (`shrine-upgrade.ts`, ADR-096's amendment of
 * 2026-10-07).
 */

/**
 * The fields a place to travel to carries beside its name (ADR-137 decisions 2 and 3): `knownFromStart`, which every
 * character knows from its join without walking to it, and `town`, which a trip to costs no coin from a shrine.
 */
const PLACE_PROPERTIES = { [KNOWN_FROM_START_FIELD]: { type: "boolean", title: "Known from the start", default: false }, [TOWN_FIELD]: { type: "boolean", title: "Town", default: false } } as const;

/**
 * The fields a shrine carries (ADR-096 decision 6): its name, and `charges` and `lit`,
 * which nothing reads and every path carries as they are, kept for the author's later attunement design, `start`,
 * "Starting shrine", which marks the one shrine of a map every character knows without walking to it (ADR-096's
 * amendment of 2026-10-05), and the place fields.
 */
const SHRINE_PROPERTIES = { charges: { type: "integer", title: "Charges", default: 3 }, lit: { type: "boolean", title: "Lit", default: false }, [SHRINE_START_FIELD]: { type: "boolean", title: "Starting shrine", default: false }, ...PLACE_PROPERTIES } as const;

/**
 * The fields a lantern carries (ADR-058 decisions 2 and 7): what powers it, `none`, `mundane` or `arcane`, and its
 * capacity, a whole number of 0 or more. Both are authored and compiled into the package, and no rule reads either:
 * they record which lanterns an ability may later draw on, so that no world needs editing again when one does.
 */
const LANTERN_SCHEMA = {
  type: "object",
  properties: { powerSource: { type: "string", title: "Power source", enum: ["none", "mundane", "arcane"], default: "none" }, capacity: { type: "integer", title: "Capacity", default: 0, minimum: 0 } },
  required: ["powerSource", "capacity"],
} as const;

const ENTITY_TYPES: readonly EntityTypeDefinition[] = [
  {
    type: SPAWN_POINT,
    label: "Spawn point",
    serverOnly: false,
    schema: { type: "object", properties: { facing: { type: "string", title: "Facing", enum: ["up", "down", "left", "right"], default: "down" }, label: { type: "string", title: "Label" } }, required: ["facing"] },
  },
  { type: CREATURE_SPAWN, label: "Creature spawn", serverOnly: true, schema: CREATURE_SPAWN_SCHEMA },
  { type: NPC, label: "NPC", serverOnly: true, schema: NPC_SCHEMA },
  {
    type: ARRIVAL_POINT,
    label: "Arrival point",
    serverOnly: true,
    schema: { type: "object", properties: { name: { type: "string", title: "Name", default: "", maxLength: ARRIVAL_NAME_MOST }, facing: { type: "string", title: "Facing", enum: ["up", "down", "left", "right"], default: "down" } }, required: ["name", "facing"] },
  },
  { type: QUEST_POINT, label: "Quest point", serverOnly: true, schema: QUEST_POINT_SCHEMA },
  { type: FIELD, label: "Field", serverOnly: true, schema: FIELD_SCHEMA },
];

/**
 * The kinds of thing kept out of the border band, one chunk from an edge with a placed neighbour (ADR-109
 * decision 6, brief 4.7 rule 2): a shrine, a Spawn point, a creature spawn and any other thing with a behaviour, so
 * a crowd or a fight never straddles two map servers. A shrine comes first so it is reported as one and not twice.
 */
const BORDER_BAND: readonly BorderBandKind[] = [
  { id: "shrine", label: "Shrine", objectTypes: [SHRINE] },
  { id: "spawn-point", label: "Spawn point", entityTypes: [SPAWN_POINT] },
  { id: "creature-spawn", label: "Creature spawn", entityTypes: [CREATURE_SPAWN] },
  { id: "behaviour", label: "Thing with a behaviour", anyBehaviour: true },
];

/**
 * The zone type a shrine's stamp paints (ADR-096 decisions 35 and 38): a Sanctuary, one of `ZONE_TYPES`.
 */
const SHRINE_ZONE = "sanctuary";

/**
 * The zone type a new room's floor is painted with (ADR-122 decision 3.1, the author's answer 1: the inn is
 * "Safe, like a Sanctuary"): one of `ZONE_TYPES`.
 */
const ROOM_ZONE = "sanctuary";

/**
 * The Grassland 2.0 prop sheet the packs share, and the frames a `sign` and a `well` had while they were
 * entity types (ADR-042). `world:migrate` carries a placed one over to a prop of the same frames.
 */
export const PROPS_SHEET_1 = "grass-land-2.0:Atlas-Props-sheet1-sprites";

/**
 * The frames the entity form of each object type showed per state, so a world saved before ADR-053 keeps
 * the art it had when its entities become behaviour props. The well's `raisedEmpty` state, which that form never
 * had, rests where `raised` does, since the sheet draws no water (ADR-042's amendment of 2026-10-03), so a well
 * carried over has a frame for every state and compiles.
 */
export const LEGACY_OBJECT_FRAMES: Readonly<Record<string, Readonly<Record<string, { readonly tileset: string; readonly tile: number }>>>> = {
  [SIGN]: { standing: { tileset: PROPS_SHEET_1, tile: 480 } },
  [WELL]: { raised: { tileset: PROPS_SHEET_1, tile: 591 }, lowered: { tileset: PROPS_SHEET_1, tile: 587 }, raisedEmpty: { tileset: PROPS_SHEET_1, tile: 591 } },
};

/**
 * The object types (ADR-053): behaviour only, with no art. The author picks the frame by placing the prop;
 * a state past the initial one shows the frame the behaviour names. Every verb is a transition (ADR-058):
 * a sign reads without changing state; a well starts lowered, its bucket comes up full by the strip the prop names
 * `water`, and goes down by that strip reversed, or by the `empty` strip reversed for a bucket raised empty (ADR-042's
 * amendment of 2026-10-03). No verb enters `raisedEmpty`: `raised` to `raisedEmpty` is the hook for the verb that
 * takes the water (P1.6-T80). Each state blocks by the footprint of the frame it rests on, so a state blocks
 * differently only when its frame's footprint does (ADR-053's amendment of 2026-10-03); a gate opens and closes, each
 * state blocking by the footprint of the frame it rests on (a closed gate
 * its whole span, an open one its posts), and each move plays the strip the prop names for it. A roof is
 * driven by presence (ADR-058 amendment, P1.3-T4): it opens on a player's own screen while that player's
 * character stands under it and closes on leaving, blocks nothing, has no entity kind and sends nothing, so
 * its verbs only name the strips the chooser offers. A training dummy is driven by blows (ADR-058's amendment for
 * P2.2-T7): a character's blow flips it between its two struck states, each change playing the hit strip the prop
 * names, and it rests back to standing; every state rests on the standing frame, so its verbs too only name strips.
 * A chest (ADR-091 decision 17) is closed or open for everyone alike: `open` from closed plays the opening strip, `open`
 * on an open chest changes nothing anyone sees and is how a later player asks for their own loot, declared before
 * `shut` so an open chest offers `open`; `shut` is the map server's, a while after the last open. Its one field names
 * the loot table it rolls, chosen from the library's list.
 * A shrine (ADR-096 decisions 1 to 6) is driven by neither: the map server never changes its state, each viewer's page
 * draws it `available` or `dormant` by what its own character has discovered, and an arrival pops it by an event. It
 * stands `available` on the placed frame, the first of a "buff available" loop, and rests `dormant` on its obelisk's
 * still, or on the last frame of the strip named `pop` where the tileset lists no still; both of its transitions name
 * that strip, so the chooser lists it once, and neither is a verb a player uses (`offersNoVerb`). Both states block
 * by the frame they rest on. Its zone stamp is its reach painted as Sanctuary (decision 38): the editor's "Paint its
 * Sanctuary zone" lays `SHRINE_REACH` around the stone's base and makes the same cells the ring it is discovered by.
 * A lantern (ADR-058 decisions 1 and 7) is unlit or lit for everyone alike: `light` from unlit, `douse` from lit. Neither
 * names a strip, since the packs draw a lantern as two stills, so the change is a cut; each state blocks by the frame
 * it rests on. Its power source and capacity are fields nothing reads (`LANTERN_SCHEMA`).
 */
/**
 * A gate's fields: its Lock and Key, then its Sound set (ADR-085's amendment of 2026-10-13), a required choice
 * that defaults to the wooden door, so a gate saved before the field reads as one and is written nothing.
 */
const GATE_SOUND_SET = soundSetProperty(GATE);
const GATE_FIELDS: JsonSchema = GATE_SOUND_SET === null ? GATE_SCHEMA : { ...GATE_SCHEMA, properties: { ...GATE_SCHEMA.properties, [SOUND_SET_FIELD]: GATE_SOUND_SET }, required: [...(GATE_SCHEMA.required ?? []), SOUND_SET_FIELD] };

const OBJECT_TYPES: readonly ObjectTypeDefinition[] = [
  {
    type: SIGN,
    label: "Sign",
    states: [{ id: "standing", footprint: "auto" }],
    initial: "standing",
    transitions: [{ verb: "read", from: "standing", to: "standing" }],
    schema: { type: "object", properties: { text: { type: "string", title: "Text", maxLength: MAX_INTERACTION_TEXT_CHARS }, layout: { type: "string", title: "Long text", enum: READING_LAYOUTS, default: DEFAULT_READING_LAYOUT } }, required: ["text", "layout"] },
  },
  {
    type: WELL,
    label: "Well",
    states: [
      { id: "lowered", footprint: "auto" },
      { id: "raised", footprint: "auto" },
      { id: "raisedEmpty", footprint: "auto" },
    ],
    initial: "lowered",
    uninterruptible: true,
    transitions: [
      { verb: "raise", from: "lowered", to: "raised", plays: "water" },
      { verb: "lower", from: "raised", to: "lowered", plays: "water", reverse: true },
      { verb: "lower", from: "raisedEmpty", to: "lowered", plays: "empty", reverse: true },
    ],
    schema: { type: "object", properties: {} },
  },
  {
    type: GATE,
    label: "Gate",
    states: [
      { id: "closed", footprint: "auto" },
      { id: "open", footprint: "auto" },
    ],
    initial: "closed",
    transitions: [
      { verb: "open", from: "closed", to: "open", plays: "opening", requires: [UNLOCKED_GUARD] },
      { verb: "close", from: "open", to: "closed", plays: "closing" },
    ],
    schema: GATE_FIELDS,
    fieldGroups: AUTO_CLOSE_GROUPS,
    soundSetField: SOUND_SET_FIELD,
    choiceLabels: { [SOUND_SET_FIELD]: soundSetLabels(GATE) },
  },
  {
    type: ROOF,
    label: "Roof",
    states: [
      { id: "closed", footprint: "none" },
      { id: "open", footprint: "none" },
    ],
    initial: "closed",
    presence: { inside: "open" },
    transitions: [
      { verb: "enter", from: "closed", to: "open", plays: "opening" },
      { verb: "leave", from: "open", to: "closed", plays: "closing" },
    ],
    schema: { type: "object", properties: {} },
  },
  {
    type: DUMMY,
    label: "Training dummy",
    states: [
      { id: "standing", footprint: "auto" },
      { id: "struck", footprint: "auto" },
      { id: "struck-again", footprint: "auto" },
    ],
    initial: "standing",
    mustBlock: true,
    transitions: [
      { verb: "strike", from: "standing", to: "struck", plays: "hit" },
      { verb: "strike", from: "struck", to: "struck-again", plays: "hit" },
      { verb: "strike", from: "struck-again", to: "struck", plays: "hit" },
      { verb: "rest", from: "struck", to: "standing" },
      { verb: "rest", from: "struck-again", to: "standing" },
    ],
    schema: { type: "object", properties: {} },
  },
  {
    type: CHEST,
    label: "Chest",
    states: [
      { id: "closed", footprint: "auto" },
      { id: "open", footprint: "auto" },
    ],
    initial: "closed",
    hiddenOnMap: true,
    transitions: [
      { verb: "open", from: "closed", to: "open", plays: "opening" },
      { verb: "open", from: "open", to: "open" },
      { verb: "shut", from: "open", to: "closed", plays: "closing" },
    ],
    schema: CHEST_SCHEMA,
  },
  {
    type: SHRINE,
    label: "Shrine",
    states: [
      { id: "available", footprint: "auto" },
      { id: "dormant", footprint: "auto" },
    ],
    initial: "available",
    hiddenOnMap: true,
    zoneStamp: { zoneType: SHRINE_ZONE, columns: SHRINE_REACH.columns, rows: SHRINE_REACH.rows, originColumn: SHRINE_REACH.originColumn, originRow: SHRINE_REACH.originRow, runs: SHRINE_REACH.runs, activation: true },
    transitions: [
      { verb: "attune", from: "dormant", to: "available", plays: "pop" },
      { verb: "settle", from: "available", to: "dormant", plays: "pop" },
    ],
    schema: { type: "object", properties: { name: { type: "string", title: "Name", default: "", maxLength: MAX_RESPAWN_POINT_NAME_CHARS }, ...SHRINE_PROPERTIES }, required: ["name"] },
  },
  {
    type: LANTERN,
    label: "Lantern",
    states: [
      { id: "unlit", footprint: "auto" },
      { id: "lit", footprint: "auto" },
    ],
    initial: "unlit",
    transitions: [
      { verb: "light", from: "unlit", to: "lit" },
      { verb: "douse", from: "lit", to: "unlit" },
    ],
    schema: LANTERN_SCHEMA,
  },
  {
    type: WAYPOINT,
    label: "Waypoint",
    states: [
      { id: "available", footprint: "auto" },
      { id: "dormant", footprint: "auto" },
    ],
    initial: "available",
    hiddenOnMap: true,
    transitions: [
      { verb: "attune", from: "dormant", to: "available", plays: "pop" },
      { verb: "settle", from: "available", to: "dormant", plays: "pop" },
    ],
    schema: { type: "object", properties: { name: { type: "string", title: "Name", default: "", maxLength: MAX_RESPAWN_POINT_NAME_CHARS }, [REGION_FIELD]: { type: "string", title: "Region", default: "", maxLength: MAX_PLACE_REGION_CHARS }, ...PLACE_PROPERTIES }, required: ["name"] },
  },
];

/**
 * What each object type is called, by its id, for an Issue's words.
 */
const THING_LABELS: Readonly<Record<string, string>> = Object.freeze(Object.fromEntries(OBJECT_TYPES.map((type) => [type.type, type.label])));

for (const type of OBJECT_TYPES) {
  if (type.states.length > MAX_ANIM + 1) {
    throw new Error(`object type ${type.type} declares ${type.states.length} states; the snapshot carries at most ${MAX_ANIM + 1}`);
  }
  if (type.presence === undefined && objectKindOf(type.type) === undefined) {
    throw new Error(`object type ${type.type} has no entity kind; add it to EntityKind`);
  }
  assertVerbNames(type);
}

/**
 * The map OdysseyTales starts players on, which the game server runs by default and the editor never removes.
 */
export const START_MAP = "main";

/**
 * Reports each training dummy whose struck state rests on a frame other than the one it stands on (ADR-058's
 * amendment for P2.2-T7): a blow must never change a dummy's footprint or its picture at rest. A struck state with
 * no frame is Daedalus's own refusal.
 *
 * @param map The map.
 * @returns One issue per such state.
 */
function dummyIssues(map: MapDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour?.type !== DUMMY) {
        continue;
      }
      for (const [stateId, frame] of Object.entries(prop.behaviour.frames ?? {})) {
        if (frame.tileset !== prop.frame.tileset || frame.tile !== prop.frame.tile) {
          issues.push({ mapId: map.id, kind: "dummy.rest-frame", layerId: layer.id, propId: prop.id, message: `prop ${prop.id} is a Training dummy whose ${stateId} state rests on another frame than the one it stands on; rest every state on the standing frame` });
        }
      }
    }
  }
  return issues;
}

/**
 * The rules a gate's key is checked against, read once on the first validation that needs them.
 */
let rulesForLocks: Pick<Rules, "items"> | null = null;

/**
 * The rules' items, read once.
 *
 * @returns The rules, as `loadRules` answers them.
 */
function itemRules(): Pick<Rules, "items"> {
  rulesForLocks ??= loadRules();
  return rulesForLocks;
}

/**
 * Reports a map that has no spawn point on any entity layer, in Daedalus's form: what is wrong, then `; ` and
 * the fix, which the editor's Issues list shows apart. The start map is refused without one; another map is
 * only warned, since players reach it by travel (P1.4-T25) and no map server runs it before then (ADR-070's
 * amendment "several maps", decision 12), and a map placed on the world grid is not even warned, since a map
 * server runs only for a map that needs one and a blank placed map holds no marker (ADR-109 decision 12). A room
 * (a map with a host) and a map with an arrival point are not warned either, since players land at an arrival
 * point through a door or a travel (ADR-122 decision 1). Every
 * placed area is refused until `world:migrate` paints it as zone cells (ADR-054's amendment of 2026-09-27,
 * decision 9), so a world whose Hostile areas would otherwise vanish never compiles, and so is every training dummy whose struck state rests on another frame (`dummyIssues`), every
 * creature spawn whose fields are out of bounds (`spawnIssues`, ADR-082 decision 3) and every chest that names no
 * loot table (`chestIssues`, ADR-091 decision 18), and every gate whose Lock and Key do not make a whole lock
 * (`lockIssues`, ADR-058's amendment of 2026-10-10), every gate whose auto-close fields hold a value no field takes (`autoCloseIssues`, ADR-058's amendment of 2026-10-31). A map that marks two shrines
 * Known from the start is refused (`shrineStartIssues`), and so is each shrine past the 64 a map holds, while a shrine
 * with no name, a repeated name, no pop strip, no painted ring or a standing spot off safe ground is warned
 * (`shrineIssues`, ADR-096 decision 41). A door that lets fighters through into a Sanctuary or into a room nobody can
 * follow into is warned, ignorably (`doorCombatIssues`, ADR-122 decision 2.5).
 *
 * @param map The map.
 * @param scope The world's tile size and compile context, which a shrine's standing spot is read with, and the world,
 *   which a door's destination is read from; each check is left out without what it reads.
 * @returns The issues: each placed area, each dummy resting elsewhere, each chest with no table, each bad lock, each
 *   Sound set that is none of its type's, each bad creature
 *   spawn field, each bad NPC or quest point field and quest point place used twice, each of two starting shrines, the
 *   shrine checks, then a missing spawn point.
 */
function validate(map: MapDocument, scope?: ValidationScope): readonly ValidationIssue[] {
  const spawns = map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((e) => e.type === CREATURE_SPAWN).flatMap((e) => spawnIssues(map.id, layer.id, e)) : []));
  const npcs = map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((e) => e.type === NPC).flatMap((e) => npcIssues(map.id, layer.id, e)) : []));
  const areas = [...map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((e) => e.type === AREA).map((e): ValidationIssue => ({ mapId: map.id, kind: "area.placed", layerId: layer.id, entityId: e.id, message: `area ${e.id} is a placed area, which zones replaced; run bun run world:migrate to paint it as zone cells` })) : [])), ...dummyIssues(map), ...chestIssues(map), ...lockIssues(map, itemRules()), ...autoCloseIssues(map, (fields) => lockOf(fields) !== null), ...soundSetIssues(map, THING_LABELS), ...spawns, ...npcs, ...questPointIssues(map), ...fieldIssues(map, scope?.tileSize), ...shrineStartIssues(map), ...shrineIssues(map, scope), ...arrivalIssues(map), ...(scope?.world === undefined ? [] : doorCombatIssues(map, scope.world))];
  const spawnPoint = map.layers.some((l) => l.kind === "entity" && l.entities.some((e) => e.type === SPAWN_POINT));
  if (spawnPoint) {
    return areas;
  }
  const arrivalPoint = map.layers.some((l) => l.kind === "entity" && l.entities.some((e) => e.type === ARRIVAL_POINT));
  if (map.id !== START_MAP && (map.place !== undefined || map.host !== undefined || arrivalPoint)) {
    return areas;
  }
  return [...areas, map.id === START_MAP ? { mapId: map.id, kind: "map.no-spawn", message: `map ${map.id} has no ${SPAWN_POINT}; place a Spawn point on a marker layer` } : { mapId: map.id, kind: "map.no-spawn", ignorable: true, severity: "warning", message: `map ${map.id} has no ${SPAWN_POINT}; place a Spawn point on a marker layer before a map server runs it` }];
}

/**
 * The OdysseyTales adapter; hand it to `EditorStore` and derive the compile context's entity types from it.
 * Its stand-in carries no art: the editor page adds the player's frame once it has read the character
 * manifest (ADR-071 decision 1).
 */
export const ODYSSEY_ADAPTER: DaedalusGameAdapter = { entityTypes: ENTITY_TYPES, objectTypes: OBJECT_TYPES, collision: collisionRulesFor, collisionBrushes: COLLISION_BRUSHES, footprintPolicy: PROP_FOOTPRINT_RULES, propGround: propIsGround, propNoFade: propIsBuilding, groundSurface: (ground) => groundSurface(ground.name), surfaceNames: SURFACES.map((surface) => surface.id), suggestFrame: suggestFrameName, suggestAnimation: suggestAnimationName, validate, standIn: ODYSSEY_STAND_IN, camera: ODYSSEY_CAMERA, startMap: START_MAP, zoneTypes: ZONE_TYPES, borderBand: BORDER_BAND, seamGround: seamGroundOf, density: ODYSSEY_DENSITY, roomZone: ROOM_ZONE, atmosphere: { music: MAP_MUSIC, ambience: MAP_AMBIENCE }, arrivalType: ARRIVAL_POINT, doorFields: DOOR_FIELDS, mapGroupLabel: REGION_LABEL };
